import { ReactElement } from 'react';
import {
  ActionRow,
  CustomErrorLabel,
  ErrorMessage,
  GreenSpan,
  HeaderRow,
  MethodText,
  MobileContainer,
  ModalImage,
  ResultDescriptionContainer,
  ResultThirdPageText,
  Row,
  RowWithError,
  Stepper,
  StepperContainer,
  StepText,
  WrapActionRow,
} from './styles';
import { CloseMark } from '@root/src/assets/images';
import ResultImageUpload from './upload/imageUpload/ResultImageUpload';
import Lang from '@lang/en';
import DropDown from '@designSystem/dropDown';
import Button from '@root/src/app/designSystem/buttons';
import { Header } from '@designSystem/typings';
import { onDateChangeHandler, ResultsComponentI } from '.';
import ResultMobileImage from './resultMobileImage';
import { Modal } from 'react-bootstrap';
import DateField from './sampleDateTimeField/DateField';

const ResultsComponentMobile = ({
  orderId,
  setImageUrl,
  clearImageUrl,
  imageUrl,
  method,
  setMethod,
  page,
  fullName,
  error,
  handleClose,
  handleSave,
  handleConfirm,
  isDisabledConfirm,
  isDisabledSave,
  onResultChange,
  resultOptions,
  closePreviewModal,
  openPreviewModal,
  setZoomImage,
  zoomImage,
  setIsPreviewModalOpened,
  isPreviewModalOpened,
  testDate,
  testHours,
  testMinutes,
  setTestDate,
  setTestHours,
  setTestMinutes,
  reportedDate,
  reportedHours,
  reportedMinutes,
  setReportedDate,
  setReportedHours,
  setReportedMinutes,
  reportedDateEarlierTestDate,
  currentDateEarlierSelectedDate,
}: ResultsComponentI): ReactElement => {
  const errorMessage =
    reportedDateEarlierTestDate || currentDateEarlierSelectedDate;

  return (
    <>
      <Modal
        data-testid="modal-window-order-page-preview-image"
        dialogClassName="resultsPreviewFullscreen"
        fullscreen="true"
        onHide={() => setIsPreviewModalOpened(false)}
        show={isPreviewModalOpened}
      >
        <Button type="close" onClick={() => closePreviewModal()} />
        <ModalImage
          onClick={() => setZoomImage(!zoomImage)}
          src={imageUrl}
          zoomImage={zoomImage}
        />
      </Modal>
      <MobileContainer>
        <HeaderRow>
          <Header data-testid="healthcheck-order-add-result-header">
            {Lang.healthCheckAddResultsText}
          </Header>
          <CloseMark onClick={handleClose} />
        </HeaderRow>
        {page !== 3 ? (
          <>
            <ResultDescriptionContainer>
              <span>{Lang.healthCheckAddResultsDescriptionText}</span>{' '}
              <GreenSpan>{fullName}</GreenSpan>
            </ResultDescriptionContainer>
          </>
        ) : (
          <ResultThirdPageText>
            {Lang.healthCheckConfirmMethodText}
          </ResultThirdPageText>
        )}
        <WrapActionRow isMobile>
          <StepperContainer page={page}>
            <Stepper />
            <Stepper />
            <Stepper />
          </StepperContainer>
          <StepText>{`Step ${page} of 3`}</StepText>
        </WrapActionRow>
        {page === 1 && (
          <RowWithError>
            <Row>
              <DateField
                dateLabel={Lang.healthCheckAddDateLabel}
                timeLabel={Lang.healthCheckAddTimeLabel}
                date={testDate}
                onDateChange={(value): void =>
                  onDateChangeHandler(value, setTestDate)
                }
                onHoursChange={setTestHours}
                hours={testHours}
                onMinutesChange={setTestMinutes}
                minutes={testMinutes}
                errorMessage={errorMessage}
                testId="result"
              />
            </Row>
            <Row>
              <DateField
                dateLabel={Lang.healthCheckReportedDateLabel}
                timeLabel={Lang.healthCheckReportedTimeLabel}
                date={reportedDate}
                onDateChange={(value): void =>
                  onDateChangeHandler(value, setReportedDate)
                }
                onHoursChange={setReportedHours}
                hours={reportedHours}
                onMinutesChange={setReportedMinutes}
                minutes={reportedMinutes}
                errorMessage={errorMessage}
                testId="reported"
              />
              {errorMessage && (
                <ErrorMessage>
                  {reportedDateEarlierTestDate
                    ? Lang.healthCheckAddResultsReportedDateEarlierTakenDateErrorText
                    : Lang.healthCheckAddResultsCurrentDateEarlierSelectedDateErrorText}
                </ErrorMessage>
              )}
            </Row>
            <CustomErrorLabel>{error}</CustomErrorLabel>
          </RowWithError>
        )}
        {page === 2 && (
          <RowWithError>
            <Row>
              <DropDown
                data-testid="healthcheck-order-add-result-dropdown"
                label="Result"
                options={resultOptions}
                onChange={(event) => {
                  event && onResultChange(event);
                }}
              />
            </Row>
            {imageUrl ? (
              <ResultMobileImage
                setIsPreviewModalOpened={openPreviewModal}
                clearImageUrl={clearImageUrl}
                imageUrl={imageUrl}
                testId="healthcheck-addResult-ResultImage"
              />
            ) : (
              <ResultImageUpload
                orderId={orderId}
                onImageUploaded={setImageUrl}
              />
            )}
          </RowWithError>
        )}
        {page === 3 && (
          <MethodText
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            data-testid="healthcheck-add-result-method-text"
          />
        )}
        <ActionRow isMobile>
          <Button
            onClick={handleClose}
            type="secondary"
            label={Lang.cancelText}
          />
          {page !== 3 && (
            <Button
              data-testid="healthcheck-order-add-results-continue-button"
              onClick={handleConfirm}
              type="primary"
              label={Lang.continueText}
              disabled={isDisabledConfirm}
            />
          )}
          {page === 3 && (
            <Button
              data-testid="healthcheck-order-add-results-save-button"
              onClick={handleSave}
              type="primary"
              label={Lang.saveText}
              disabled={isDisabledSave}
            />
          )}
        </ActionRow>
      </MobileContainer>
    </>
  );
};

export default ResultsComponentMobile;
